import React from 'react';
import { Box, Typography, SxProps, Theme } from '@mui/material';
import { panelShadow, brandIconCircleSx } from '../../theme/styleHelpers';

export interface StatCardProps {
  label: string;
  value: string | number;
  helper?: string;
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  loading?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * StatCard - A reusable card component for displaying statistics
 *
 * Features calm design with no hover lift effects to avoid suggesting
 * interactivity when the card is informational only.
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  helper,
  icon,
  color = 'primary',
  loading = false,
  sx = {},
}) => {
  return (
    <Box
      sx={[
        (theme) => ({
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: panelShadow(theme),
          height: '100%',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box>
        <Typography
          component="div"
          sx={(theme) => ({
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            marginBottom: '4px',
          })}
        >
          {label}
        </Typography>
        <Typography
          component="div"
          sx={(theme) => ({
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
            color:
              color === 'error'
                ? theme.palette.error.main
                : theme.palette.text.primary,
            lineHeight: 1,
            marginBottom: helper ? '4px' : 0,
          })}
        >
          {loading ? '…' : value}
        </Typography>
        {helper && (
          <Typography
            component="div"
            sx={(theme) => ({
              fontSize: 11,
              color: theme.palette.text.secondary,
            })}
          >
            {helper}
          </Typography>
        )}
      </Box>
      {icon && (
        <Box sx={brandIconCircleSx}>
          {icon}
        </Box>
      )}
    </Box>
  );
};

export default StatCard;
